import React, { useState } from 'react';
import { Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import PriceAnalysis from './PriceAnalysis';
import PurchasesAnalysis from './PurchasesAnalysis';
import LargePdfAnalysis from './LargePdfAnalysis';
import SimplePriceAnalysis from './SimplePriceAnalysis';
import SupplierPriceAnalysis from './SupplierPriceAnalysis';
import ProjectQuantityComparison from './ProjectQuantityComparison';
import BalanceComparison from './BalanceComparison';
import WhatsAppAnalysis from './WhatsAppAnalysis';
import { TrendingUp, Users, FileSpreadsheet, BarChart3, Building2, Layers, Scale, MessageSquare, Menu, X, ChevronDown } from 'lucide-react';

const AnalysisApp = () => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const tabs = [
    {
      path: '/analysis/simple',
      label: 'تحليل الأسعار الشامل',
      shortLabel: 'الأسعار الشامل',
      icon: BarChart3,
      color: 'emerald',
      bgColor: 'bg-emerald-500'
    },
    {
      path: '/analysis/supplier',
      label: 'تحليل الأسعار للموردين',
      shortLabel: 'أسعار الموردين',
      icon: Building2,
      color: 'purple',
      bgColor: 'bg-purple-500'
    },
    {
      path: '/analysis/project-qty',
      label: 'مقارنة كميات المشاريع',
      shortLabel: 'كميات المشاريع',
      icon: Layers,
      color: 'teal',
      bgColor: 'bg-teal-500'
    },
    {
      path: '/analysis/balance',
      label: 'مقارنة ميزان الحسابات',
      shortLabel: 'ميزان الحسابات',
      icon: Scale,
      color: 'indigo',
      bgColor: 'bg-indigo-500'
    },
    {
      path: '/analysis/whatsapp',
      label: 'تحليل طلبات واتساب',
      shortLabel: 'طلبات واتساب',
      icon: MessageSquare,
      color: 'green',
      bgColor: 'bg-green-500'
    },
    {
      path: '/analysis',
      label: 'تحليل فروقات الأسعار',
      shortLabel: 'فروقات الأسعار',
      icon: TrendingUp,
      color: 'orange',
      bgColor: 'bg-orange-500'
    },
    {
      path: '/analysis/purchases',
      label: 'مقارنة الموردين',
      shortLabel: 'مقارنة الموردين',
      icon: Users,
      color: 'pink',
      bgColor: 'bg-pink-500'
    },
    {
      path: '/analysis/large-pdf',
      label: 'تحليل PDF/Excel الكبير',
      shortLabel: 'PDF/Excel',
      icon: FileSpreadsheet,
      color: 'blue',
      bgColor: 'bg-blue-500'
    }
  ];

  const getTabStyle = (tab, isActive) => {
    if (!isActive) {
      return {
        bg: 'bg-white hover:bg-gray-50',
        text: 'text-gray-700',
        icon: 'text-gray-400',
        border: 'border-gray-200 hover:border-gray-300'
      };
    }
    switch (tab.color) {
      case 'emerald': return { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: 'text-emerald-600', border: 'border-emerald-300' };
      case 'orange': return { bg: 'bg-orange-50', text: 'text-orange-700', icon: 'text-orange-600', border: 'border-orange-300' };
      case 'purple': return { bg: 'bg-purple-50', text: 'text-purple-700', icon: 'text-purple-600', border: 'border-purple-300' };
      case 'blue': return { bg: 'bg-blue-50', text: 'text-blue-700', icon: 'text-blue-600', border: 'border-blue-300' };
      case 'teal': return { bg: 'bg-teal-50', text: 'text-teal-700', icon: 'text-teal-600', border: 'border-teal-300' };
      case 'indigo': return { bg: 'bg-indigo-50', text: 'text-indigo-700', icon: 'text-indigo-600', border: 'border-indigo-300' };
      case 'green': return { bg: 'bg-green-50', text: 'text-green-700', icon: 'text-green-600', border: 'border-green-300' };
      case 'pink': return { bg: 'bg-pink-50', text: 'text-pink-700', icon: 'text-pink-600', border: 'border-pink-300' };
      default: return { bg: 'bg-gray-50', text: 'text-gray-700', icon: 'text-gray-600', border: 'border-gray-300' };
    }
  };

  const currentTab = tabs.find(tab => 
    currentPath === tab.path || (tab.path === '/analysis' && currentPath === '/analysis/')
  ) || tabs[0];

  return (
    <div className="min-h-screen bg-gray-100" dir="rtl">
      {/* Mobile Header with Dropdown */}
      <div className="lg:hidden bg-white border-b border-gray-200 sticky top-0 z-20">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-full flex items-center justify-between px-4 py-3"
        >
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${currentTab.bgColor}`}>
              <currentTab.icon className="w-5 h-5 text-white" />
            </div>
            <span className="font-medium text-gray-800">{currentTab.label}</span>
          </div>
          <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-white border-b border-gray-200 shadow-lg max-h-[70vh] overflow-y-auto">
            {tabs.map((tab) => {
              const isActive = currentPath === tab.path || 
                (tab.path === '/analysis' && currentPath === '/analysis/');
              const Icon = tab.icon;
              const style = getTabStyle(tab, isActive);
              
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 ${style.bg} ${style.text}`}
                >
                  <div className={`p-2 rounded-lg ${tab.bgColor}`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-medium">{tab.label}</span>
                  {isActive && (
                    <div className="mr-auto w-2 h-2 rounded-full bg-current" />
                  )}
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop Navigation - Grid Layout */}
      <div className="hidden lg:block bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="grid grid-cols-4 gap-2">
            {tabs.map((tab) => {
              const isActive = currentPath === tab.path || 
                (tab.path === '/analysis' && currentPath === '/analysis/');
              const Icon = tab.icon;
              const style = getTabStyle(tab, isActive);
              
              return (
                <Link
                  key={tab.path}
                  to={tab.path}
                  className={`
                    flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all
                    ${style.bg} ${style.text} ${style.border}
                    ${isActive ? 'shadow-sm font-medium' : ''}
                  `}
                >
                  <div className={`p-1.5 rounded-md ${tab.bgColor}`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm truncate">{tab.shortLabel}</span>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Overlay for mobile menu */}
      {mobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/20 z-10"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Routes */}
      <Routes>
        <Route path="/" element={<PriceAnalysis />} />
        <Route path="/simple" element={<SimplePriceAnalysis />} />
        <Route path="/supplier" element={<SupplierPriceAnalysis />} />
        <Route path="/project-qty" element={<ProjectQuantityComparison />} />
        <Route path="/balance" element={<BalanceComparison />} />
        <Route path="/whatsapp" element={<WhatsAppAnalysis />} />
        <Route path="/purchases" element={<PurchasesAnalysis />} />
        <Route path="/large-pdf" element={<LargePdfAnalysis />} />
        <Route path="*" element={<Navigate to="/analysis" replace />} />
      </Routes>
    </div>
  );
};

export default AnalysisApp;
